import { useState } from 'react';
import { useJobs } from '../../jobs/hooks/useJobs';
import { useInvoices } from '../../invoices/hooks/useInvoices';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar, 
  Clock, 
  DollarSign, 
  Star, 
  MapPin, 
  User, 
  FileText, 
  CreditCard, 
  Plus
} from 'lucide-react';
import { DateUtils } from '@cleanpro/common-ui';

interface User {
  id: string;
  email: string;
  role?: string;
  fullName?: string;
}

interface CustomerDashboardContentProps {
  user: User;
}

export default function CustomerDashboardContent({ user }: CustomerDashboardContentProps) {
  const { jobs } = useJobs();
  const { invoices } = useInvoices();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('jobs');

  const customerJobs = jobs.filter(b => b.customerId === user.id);
  const customerInvoices = invoices.filter(i => i.customerId === user.id);
  const upcomingJobs = customerJobs.filter(b => b.status === 'confirmed');
  const pastJobs = customerJobs.filter(b => b.status === 'completed');

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'in-progress':
        return 'bg-blue-100 text-blue-800';
      case 'confirmed':
        return 'bg-yellow-100 text-yellow-800';
      case 'requested':
        return 'bg-purple-100 text-purple-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Upcoming Jobs</p>
              <p className="text-3xl font-bold text-gray-900">{upcomingJobs.length}</p>
            </div>
            <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Calendar className="h-6 w-6 text-indigo-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Services</p>
              <p className="text-3xl font-bold text-gray-900">{pastJobs.length}</p>
            </div>
            <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
              <FileText className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Outstanding Balance</p>
              <p className="text-3xl font-bold text-gray-900">
                ${customerInvoices.filter(i => i.status === 'pending').reduce((sum, i) => sum + i.totalAmount, 0)}
              </p>
            </div>
            <div className="h-12 w-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <DollarSign className="h-6 w-6 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-100">
          <h2 className="text-lg font-medium text-gray-800">Quick Actions</h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={() => navigate('/book-service')}
              className="flex items-center p-4 border border-gray-200 rounded-lg hover:border-green-300 hover:bg-green-50 transition-colors"
            >
              <Plus className="h-8 w-8 text-green-600 mr-3" />
              <div className="text-left">
                <h3 className="font-medium text-gray-900">Book New Service</h3>
                <p className="text-sm text-gray-600">Schedule a cleaning appointment</p>
              </div>
            </button>
            
            <button
              onClick={() => navigate('/my-jobs')}
              className="flex items-center p-4 border border-gray-200 rounded-lg hover:border-green-300 hover:bg-green-50 transition-colors"
            >
              <Calendar className="h-8 w-8 text-green-600 mr-3" />
              <div className="text-left">
                <h3 className="font-medium text-gray-900">My Jobs</h3>
                <p className="text-sm text-gray-600">View and manage appointments</p>
              </div>
            </button>
            
            <button
              onClick={() => navigate('/profile')}
              className="flex items-center p-4 border border-gray-200 rounded-lg hover:border-green-300 hover:bg-green-50 transition-colors"
            >
              <User className="h-8 w-8 text-green-600 mr-3" />
              <div className="text-left">
                <h3 className="font-medium text-gray-900">My Profile</h3>
                <p className="text-sm text-gray-600">Update account settings</p>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex">
            <button
              onClick={() => setActiveTab('jobs')}
              className={`py-4 px-6 text-sm font-medium border-b-2 ${
                activeTab === 'jobs'
                  ? 'border-green-500 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              My Jobs
            </button>
            <button
              onClick={() => setActiveTab('invoices')}
              className={`py-4 px-6 text-sm font-medium border-b-2 ${
                activeTab === 'invoices'
                  ? 'border-green-500 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Invoices & Payments
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`py-4 px-6 text-sm font-medium border-b-2 ${
                activeTab === 'history'
                  ? 'border-green-500 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Service History
            </button>
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'jobs' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Upcoming Services</h2>
                <button
                  onClick={() => navigate('/book-service')}
                  className="text-green-600 hover:text-green-700 font-medium"
                >
                  Schedule New Service →
                </button>
              </div>
              
              {upcomingJobs.length > 0 ? (
                <div className="grid gap-4">
                  {upcomingJobs.map((job) => (
                    <div key={job.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <h3 className="font-semibold text-gray-900">{job.serviceName}</h3>
                            <span className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${getStatusColor(job.status)}`}>
                              {job.status}
                            </span>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                            <div className="flex items-center space-x-2">
                              <Calendar className="h-4 w-4" />
                              <span>{DateUtils.formatDateLong(job.time)}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Clock className="h-4 w-4" />
                              <span>{DateUtils.formatTimeFromDate(job.time)} ({job.duration}h)</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <User className="h-4 w-4" />
                              <span>{job.employeeName}</span>
                            </div>
                          </div>
                          <div className="flex items-start space-x-2 mt-2 text-sm text-gray-600">
                            <MapPin className="h-4 w-4 mt-0.5" />
                            <span>{job.address}</span>
                          </div>
                          {job.notes && (
                            <div className="mt-2 p-2 bg-gray-50 rounded text-sm text-gray-600">
                              <strong>Notes:</strong> {job.notes}
                            </div>
                          )}
                        </div>
                        <div className="text-right ml-4">
                          <div className="flex items-center">
                            <DollarSign className="h-4 w-4" />
                            <span className="font-semibold text-gray-900">{job.price}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 mb-4">No upcoming jobs</p>
                  <button
                    onClick={() => navigate('/book-service')}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors inline-flex items-center space-x-2"
                  >
                    <Plus className="h-4 w-4" />
                    <span>Schedule Your First Service</span>
                  </button>
                </div>
              )}
            </div>
          )}

          {activeTab === 'invoices' && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-gray-900">Invoices & Payments</h2>
              
              {customerInvoices.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {customerInvoices.map((invoice) => (
                        <tr key={invoice.id}>
                          <td className="px-4 py-4 text-sm font-medium text-gray-900">#{invoice.id}</td>
                          <td className="px-4 py-4 text-sm text-gray-600">{invoice.issueDate}</td>
                          <td className="px-4 py-4 text-sm font-medium text-gray-900">${invoice.amount}</td>
                          <td className="px-4 py-4">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${
                              invoice.status === 'paid' ? 'bg-green-100 text-green-800' :
                              invoice.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {invoice.status}
                            </span>
                          </td>
                          <td className="px-4 py-4">
                            {invoice.status === 'pending' && (
                              <button className="text-green-600 hover:text-green-700 font-medium flex items-center space-x-1">
                                <CreditCard className="h-4 w-4" />
                                <span>Pay Now</span>
                              </button>
                            )}
                            {invoice.status === 'paid' && (
                              <button className="text-indigo-600 hover:text-blue-700 font-medium">
                                Download
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No invoices yet</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'history' && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-gray-900">Service History</h2>
              
              {pastJobs.length > 0 ? (
                <div className="grid gap-4">
                  {pastJobs.map((job) => (
                    <div key={job.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <h3 className="font-semibold text-gray-900">{job.serviceName}</h3>
                            {job.rating && (
                              <div className="flex items-center">
                                <Star className="h-4 w-4 text-yellow-400 fill-current" />
                                <span className="text-sm text-gray-600 ml-1">{job.rating}</span>
                              </div>
                            )}
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600 mb-2">
                            <div className="flex items-center space-x-2">
                              <Calendar className="h-4 w-4" />
                              <span>{DateUtils.formatDateLong(job.time)}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Clock className="h-4 w-4" />
                              <span>{DateUtils.formatTimeFromDate(job.time)}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <User className="h-4 w-4" />
                              <span>{job.employeeName}</span>
                            </div>
                          </div>
                          {job.feedback && (
                            <div className="mt-2 p-2 bg-gray-50 rounded text-sm text-gray-600">
                              <strong>Your feedback:</strong> {job.feedback}
                            </div>
                          )}
                        </div>
                        <div className="text-right ml-4">
                          <div className="flex items-center">
                            <DollarSign className="h-4 w-4" />
                            <span className="font-semibold text-gray-900">{job.price}</span>
                          </div>
                          {!job.rating && (
                            <button className="mt-2 text-sm text-indigo-600 hover:text-blue-700">
                              Rate Service
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Clock className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No service history yet</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
